/**
 * Export générique d'une liste d'objets vers un fichier Excel (.xlsx).
 * Une ligne = un objet. En-tête figé, filtres automatiques, largeurs réglables.
 * Pensé pour les tableaux croisés dynamiques : les dates et nombres sont
 * écrits comme de vraies valeurs typées (pas du texte).
 *
 * @param {object}   params
 * @param {string}   params.sheetName  Nom de l'onglet
 * @param {string}   params.fileName    Nom du fichier (.xlsx ajouté si absent)
 * @param {Array<{header:string,key:string,width?:number,type?:'text'|'number'|'date',fill?:(value:any,row:object)=>(string|null)}>} params.columns
 * @param {Array<object>} params.rows   Lignes (clés = key des colonnes)
 * @param {string}  [params.title]       Titre optionnel affiché au-dessus du tableau
 * @param {string}  [params.headerColor] Couleur ARGB de l'en-tête (défaut bleu)
 */
export async function exportRowsToExcel({ sheetName, fileName, columns, rows, title, headerColor }) {
    const HEADER_ARGB = headerColor || 'FF4472C4';
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'SPOT';
    wb.created = new Date();

    const ws = wb.addWorksheet(sheetName || 'Export', {
        views: [{ state: 'frozen', ySplit: title ? 2 : 1 }],
    });

    let headerRowIdx = 1;

    if (title) {
        const tCell = ws.getCell('A1');
        tCell.value = title;
        tCell.font = { name: 'Calibri', size: 13, bold: true, color: { argb: 'FF0F172A' } };
        ws.mergeCells(1, 1, 1, columns.length);
        ws.getRow(1).height = 22;
        headerRowIdx = 2;
    }

    // En-tête
    const headerRow = ws.getRow(headerRowIdx);
    columns.forEach((col, i) => {
        const c = headerRow.getCell(i + 1);
        c.value = col.header;
        c.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_ARGB } };
        c.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        c.border = {
            top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
        };
    });
    headerRow.height = 28;

    // Lignes de données
    rows.forEach((row, ri) => {
        const r = ws.getRow(headerRowIdx + 1 + ri);
        columns.forEach((col, ci) => {
            const c = r.getCell(ci + 1);
            let v = row[col.key];

            if (v === null || v === undefined || v === '') {
                c.value = null;
            } else if (col.type === 'date') {
                const d = v instanceof Date ? v : new Date(v);
                c.value = Number.isFinite(d.getTime()) ? d : null;
                c.numFmt = 'dd/mm/yyyy';
            } else if (col.type === 'number') {
                const n = typeof v === 'number' ? v : parseFloat(v);
                c.value = Number.isFinite(n) ? n : null;
            } else {
                c.value = String(v);
            }

            c.alignment = { vertical: 'middle', horizontal: col.type === 'text' || !col.type ? 'left' : 'center', wrapText: false };
            c.border = {
                bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } },
                right: { style: 'hair', color: { argb: 'FFE2E8F0' } },
            };
            // Couleur de cellule personnalisée (ex. statut), sinon zébrage léger
            const customFill = typeof col.fill === 'function' ? col.fill(v, row) : null;
            if (customFill) {
                c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: customFill } };
                c.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF1E293B' } };
            } else if (ri % 2 === 1) {
                c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF6F8FC' } };
            }
        });
    });

    // Largeurs
    columns.forEach((col, i) => {
        ws.getColumn(i + 1).width = col.width || 16;
    });

    // Filtres automatiques sur l'en-tête
    ws.autoFilter = {
        from: { row: headerRowIdx, column: 1 },
        to: { row: headerRowIdx, column: columns.length },
    };

    // Téléchargement
    const finalName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default exportRowsToExcel;
